import { useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Shield, Users, Building2, DoorOpen, ArrowRight, X, MessageCircle, Check } from 'lucide-react';

const WHATSAPP_NUMBER = "+41779938989";

const services = [
  {
    id: 'personenschutz',
    icon: Shield,
    title: 'Personenschutz',
    subtitle: 'Persönlicher Schutz',
    description: 'Ihr Begleitschutz im Alltag, auf Reisen oder bei Events. Diskret und immer an Ihrer Seite.',
    image: '/assets/images/service-1.jpg',
    features: ['Begleitschutz', 'Reisebegleitung', 'Sicherheitsanalyse', '24/7 Verfügbarkeit'],
    detailedInfo: {
      intro: 'Schutz für alles, was Ihnen wichtig ist. Wir sorgen mit Erfahrung und guter Planung für Ihre Sicherheit.',
      services: [
        { title: 'Persönliche Begleitung', desc: 'Schutz durch erfahrene Sicherheitskräfte.' },
        { title: 'Reisebegleitung', desc: 'Sicherheit auf allen Wegen – weltweit.' },
        { title: 'Vorbereitung', desc: 'Wir prüfen Hotels und Routen, bevor Sie ankommen.' },
        { title: 'Notfallmanagement', desc: 'Schnelle Hilfe, wenn es darauf ankommt.' },
      ],
      ideal: 'Führungskräfte, Personen des öffentlichen Lebens und Familien.'
    }
  },
  {
    id: 'veranstaltung',
    icon: Users,
    title: 'Veranstaltung',
    subtitle: 'Eventsicherheit',
    description: 'Sicherheit für Ihr Event – von der Planung bis zur Durchführung. Damit sich Ihre Gäste wohlfühlen.',
    image: '/assets/images/service-2.jpg',
    features: ['Einlasskontrolle', 'Besucherlenkung', 'VIP-Betreuung', 'Ordnungsdienst'],
    detailedInfo: {
      intro: 'Wir planen und koordinieren die gesamte Sicherheit für Ihr Event, damit Sie und Ihre Gäste unbeschwert feiern können.',
      services: [
        { title: 'Einlasskontrolle', desc: 'Professionelle Kontrolle am Eingang für einen reibungslosen Ablauf.' },
        { title: 'Besucherlenkung', desc: 'Sichere Führung von Menschenmengen und Vermeidung von Stau.' },
        { title: 'VIP-Betreuung', desc: 'Diskreter Schutz für besondere Gäste und Bereiche.' },
        { title: 'Ordnungsdienst', desc: 'Freundliche Ansprechpartner und professionelle Deeskalation.' },
      ],
      ideal: 'Konzerte, Festivals, Firmenfeiern und private Anlässe.'
    }
  },
  {
    id: 'objektschutz',
    icon: Building2,
    title: 'Objektschutz',
    subtitle: 'Property Security',
    description: 'Kontrollierte Zugänge, regelmässige Rundgänge und schnelle Reaktion bei Alarm – Ihr Eigentum in verlässlichen Händen.',
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.1.0&q=85',
    features: ['Zugangskontrolle', 'Patrouillen', 'Alarmintervention', 'Kontrollgänge'],
    detailedInfo: {
      intro: 'Ihr Eigentum in verlässlichen Händen – mit kontrollierten Zugängen, regelmässigen Patrouillen und sofortiger Alarmreaktion.',
      services: [
        { title: 'Zugangskontrolle', desc: 'Überwachung und Kontrolle aller Zugänge zu Ihrem Objekt.' },
        { title: 'Patrouillen', desc: 'Regelmässige Rundgänge und Kontrollen im Innen- und Aussenbereich.' },
        { title: 'Alarmintervention', desc: 'Schnelle Reaktion bei Alarmmeldungen mit sofortiger Einsatzbereitschaft.' },
        { title: 'Kontrollgänge', desc: 'Systematische Überprüfung aller sicherheitsrelevanten Bereiche.' },
      ],
      ideal: 'Ideal für Privatresidenzen, Geschäftsgebäude, Industrieanlagen und temporäre Baustellen.'
    }
  },
  {
    id: 'zutritt-empfang',
    icon: DoorOpen,
    title: 'Zutritt & Empfang',
    subtitle: 'Access & Reception',
    description: 'Unser Empfangsteam verbindet Gastfreundschaft mit aufmerksamer Zugangskontrolle – damit der erste Eindruck ebenso sicher wie einladend ist.',
    image: 'https://images.unsplash.com/photo-1758448500688-3ababa93fd67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBsb2JieSUyMHJlY2VwdGlvbiUyMGx1eHVyeXxlbnwwfHx8fDE3NzEwODI5MTN8MA&ixlib=rb-4.1.0&q=85',
    features: ['Empfangsdienst', 'Besuchermanagement', 'Badge-Management', 'Arealrundgänge'],
    detailedInfo: {
      intro: 'Gastfreundschaft und Zugangskontrolle unter einem Dach – damit der erste Eindruck Ihres Unternehmens sicher und einladend zugleich ist.',
      services: [
        { title: 'Empfangsdienst', desc: 'Professioneller Lobby-, Rezeption- und Pfortendienst für erstklassigen ersten Eindruck.' },
        { title: 'Besuchermanagement', desc: 'Vollständige Besucheranmeldung mit Ausweiskontrolle und digitaler Erfassung.' },
        { title: 'Schlüssel & Badge', desc: 'Sichere Verwaltung von Schlüsseln, Zutrittskarten und Badge-Systemen.' },
        { title: 'Arealrundgänge', desc: 'Regelmässige Innen- und Aussenpatrouillen nach Ihren Vorgaben.' },
      ],
      ideal: 'Ideal für Firmenzentralen, Bürogebäude, Hotels und exklusive Wohnanlagen.'
    }
  },
];

const ServiceModal = ({ service, isOpen, onClose, onOpenWizard }) => {
  if (!isOpen || !service) return null;

  const Icon = service.icon;

  const openWhatsApp = () => {
    const message = encodeURIComponent(`Guten Tag, ich interessiere mich für ${service.title}.`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=${message}`, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0B0F19]/95 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid={`service-modal-${service.id}`}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.3 }}
        className="relative w-full max-w-3xl bg-[#111625] border border-[#1E293B] rounded-sm overflow-hidden my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Image */}
        <div className="relative h-48 md:h-64 overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111625] via-[#111625]/50 to-transparent" />

          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-[#0B0F19]/80 rounded-full text-[#F1F5F9] hover:text-[#D4AF37] transition-colors"
            data-testid="modal-close-btn"
          >
            <X size={24} />
          </button>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-sm flex items-center justify-center">
                <Icon size={20} className="text-[#0B0F19]" />
              </div>
              <span className="text-[#D4AF37] uppercase tracking-wider text-xs font-medium">
                {service.subtitle}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#F1F5F9] font-['DM_Sans']">
              {service.title}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          <p className="text-[#94A3B8] text-base leading-relaxed mb-8">
            {service.detailedInfo.intro}
          </p>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {service.detailedInfo.services.map((item, index) => (
              <div
                key={index}
                className="p-4 bg-[#0B0F19] border border-[#1E293B] rounded-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={12} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <h4 className="text-[#F1F5F9] font-semibold mb-1">{item.title}</h4>
                    <p className="text-[#94A3B8] text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Ideal For */}
          <div className="p-4 bg-gradient-to-r from-[#D4AF37]/10 to-transparent border border-[#D4AF37]/20 rounded-sm mb-8">
            <p className="text-[#F1F5F9] text-sm">
              <span className="text-[#D4AF37] font-semibold">Ideal für: </span>
              {service.detailedInfo.ideal}
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => {
                onClose();
                onOpenWizard();
              }}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
              data-testid="modal-anfrage-btn"
            >
              Unverbindliche Anfrage
            </button>
            <button
              onClick={openWhatsApp}
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="modal-whatsapp-btn"
            >
              <MessageCircle size={18} />
              WhatsApp
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ServiceCard = ({ service, index, onClick }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const Icon = service.icon;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onClick={onClick}
      className="service-card group rounded-sm cursor-pointer"
      data-testid={`service-card-${service.id}`}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-52">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="image-overlay absolute inset-0" />

        {/* Icon Badge */}
        <div className="absolute top-4 left-4 w-12 h-12 bg-[#0B0F19]/80 backdrop-blur-sm border border-[#D4AF37]/30 rounded-sm flex items-center justify-center">
          <Icon size={24} className="text-[#D4AF37]" />
        </div>

        {/* Click Indicator */}
        <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-[#D4AF37] text-[#0B0F19] text-xs font-semibold uppercase tracking-wider rounded-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
          Mehr erfahren
          <ArrowRight size={14} />
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <span className="text-[#64748B] text-xs uppercase tracking-wider">
          {service.subtitle}
        </span>
        <h3 className="text-xl md:text-2xl font-semibold text-[#F1F5F9] mt-2 mb-3 font-['DM_Sans'] group-hover:text-[#D4AF37] transition-colors">
          {service.title}
        </h3>
        <p className="text-[#94A3B8] text-sm leading-relaxed mb-4">
          {service.description}
        </p>

        {/* Features */}
        <div className="flex flex-wrap gap-2">
          {service.features.map((feature, i) => (
            <span
              key={i}
              className="px-3 py-1 bg-[#1A2030] text-[#94A3B8] text-xs rounded-sm border border-[#1E293B]"
            >
              {feature}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export const Services = ({ onOpenWizard }) => {
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true });
  const [selectedService, setSelectedService] = useState(null);

  return (
    <section id="leistungen" className="section-padding bg-[#0B0F19]" data-testid="services-section">
      <div className="container-custom">
        {/* Section Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="text-[#D4AF37] uppercase tracking-[0.3em] text-xs font-medium">
            Unsere Leistungen
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#F1F5F9] mt-4 mb-6 font-['DM_Sans']">
            Was können wir für Sie tun?
          </h2>
          <p className="text-[#94A3B8] max-w-2xl mx-auto text-base md:text-lg">
            Von persönlichem Schutz bis zum professionellen Empfangsdienst – wir bieten
            massgeschneiderte Lösungen für Ihre individuellen Anforderungen.
          </p>
        </motion.div>

        {/* Services Grid - Symmetric 2x2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
              onClick={() => setSelectedService(service)}
            />
          ))}
        </div>

        {/* Hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isHeaderInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center text-[#64748B] text-sm mt-8"
        >
          Klicken Sie auf eine Karte für mehr Informationen
        </motion.p>
      </div>

      {/* Service Modal */}
      {selectedService && (
        <ServiceModal
          service={selectedService}
          isOpen={!!selectedService}
          onClose={() => setSelectedService(null)}
          onOpenWizard={onOpenWizard}
        />
      )}
    </section>
  );
};
